import { fetchPostRepaymentRegisterRepaymentMethod } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { Frames, CardType } from 'frames-react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { BillingPostcodeField } from './formFields/BillingPostcodeField';
import { CardholderNameField } from './formFields/CardholderNameField';
import { CardNumberField } from './formFields/CardNumberField';
import { CVVField } from './formFields/CVVField';
import { ExpiryDateField } from './formFields/ExpiryDateField';
import { getFramesConfig } from './frames.config';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import styles from '../LinkDebitCard.module.css';

export type TFormValues = {
    billingPostcode: string;
    cardholderName: string;
};

export const CheckoutLinkDebitCardForm = ({
    useSectionGridLayout = true,
    setSubmitAsLoading = false,
    onSuccess,
    onError,
    onRetry,
    on3DSRequired
}: {
    useSectionGridLayout?: boolean;
    setSubmitAsLoading?: boolean;
    onSuccess: () => void;
    onError: () => void;
    onRetry: () => void;
    on3DSRequired: (redirectUrl: string) => void;
}) => {
    const stateKey = useStateKey();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        getValues,
        reset
    } = useForm<TFormValues>({
        defaultValues: {
            billingPostcode: '',
            cardholderName: ''
        }
    });
    const onSubmit = async () => {
        onRetry();

        try {
            const { token, card_type } = await Frames.submitCard();
            const response = await fetchPostRepaymentRegisterRepaymentMethod({
                stateKey: stateKey,
                body: {
                    data: {
                        token,
                        card_type: card_type as Uppercase<CardType>
                    }
                }
            });

            const redirect_url = response.data.checkout_3ds_redirect_link;

            if (redirect_url) {
                on3DSRequired(redirect_url);
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            onError();

            Frames.init();
            reset();

            return;
        }

        Frames.init();
        reset();
        onSuccess();
    };

    return (
        <form
            data-testid="checkout-link-card-form"
            className={styles.LinkDebitCardForm}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div
                className={
                    useSectionGridLayout
                        ? 'grid gap-l pt-xl min-m:grid-cols-3'
                        : styles.topRow
                }
            >
                <Frames
                    config={getFramesConfig({
                        billingPostcode: getValues('billingPostcode'),
                        cardholderName: getValues('cardholderName')
                    })}
                >
                    <CardNumberField />
                    <ExpiryDateField />
                    <CVVField />
                    <BillingPostcodeField
                        formRegister={register}
                        error={errors.billingPostcode}
                    />
                    <CardholderNameField
                        formRegister={register}
                        error={errors.cardholderName}
                    />
                </Frames>
            </div>
            <div className="mt-xl flex w-full flex-col items-center gap-m">
                <Button
                    className="w-full max-w-[300px]"
                    loading={isSubmitting || setSubmitAsLoading}
                >
                    <FormattedMessage
                        id="_.link_debit_card.link_card"
                        defaultMessage="Add your card"
                    />
                </Button>
            </div>
        </form>
    );
};
